import get from 'lodash/get';
import isObject from 'lodash/isObject';
import {
    loadScimConfiguration,
    createScimConfiguration,
    createScimConfigurationCustomValidation,
    updateScimConfigurationCustomValidation,
    regenerateAPIKey,
    removeScimConfiguration,
    updateIdPGroupPermissionsForSub,
} from './org-admin.utils';

export function getInitialState () {
    return {
        active: {},
        scimConfiguration: {},
        error: null,
    };
}

const organization = {
    namespaced: true,
    state: getInitialState(),
    mutations: {
        setActive (state, { activeOrganization }) {
            if (!isObject(activeOrganization)) {
                return;
            }

            state.active = activeOrganization;
        },
        setScimConfiguration (state, { scimConfiguration }) {
            if (!isObject(scimConfiguration)) {
                return;
            }

            state.scimConfiguration = scimConfiguration;
        },
        setError (state, { error }) {
            state.error = error;
        },
    },
    actions: {
        async loadScimConfiguration ({ commit, getters }, { organizationId, reFetch = false } = {}) {
            if (!reFetch) {
                return;
            }

            organizationId = organizationId || getters.getActive.id;

            try {
                const scimConfiguration = await loadScimConfiguration(organizationId);
                commit('setScimConfiguration', { scimConfiguration });
            } catch (error) {
                commit('setError', { error });
            }
        },
        async createScimConfiguration ({ commit, getters }, { organizationId, reFetch = false } = {}) {
            if (!reFetch) {
                return;
            }

            organizationId = organizationId || getters.getActive.id;

            try {
                const scimConfiguration = await createScimConfiguration(organizationId);
                commit('setScimConfiguration', { scimConfiguration });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async createScimConfigurationCustomValidation (
            { commit, getters },
            { organizationId, issuer, audience, jwksUri, reFetch = false } = {},
        ) {
            if (!reFetch) {
                return;
            }

            organizationId = organizationId || getters.getActive.id;

            try {
                const scimConfiguration = await createScimConfigurationCustomValidation(
                    organizationId,
                    issuer,
                    audience,
                    jwksUri,
                );
                commit('setScimConfiguration', { scimConfiguration });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async updateScimConfigurationCustomValidation (
            { commit, getters },
            { organizationId, configurationId, issuer, audience, jwksUri, reFetch = false } = {},
        ) {
            if (!reFetch) {
                return;
            }

            organizationId = organizationId || getters.getActive.id;
            configurationId = configurationId || getters.getScimConfiguration?.id;

            try {
                const scimConfiguration = await updateScimConfigurationCustomValidation(
                    organizationId,
                    configurationId,
                    issuer,
                    audience,
                    jwksUri,
                );
                commit('setScimConfiguration', { scimConfiguration });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async regenerateAPIKey ({ commit, getters }, { organizationId, configurationId, reFetch = false } = {}) {
            if (!reFetch) {
                return;
            }

            organizationId = organizationId || getters.getActive.id;
            configurationId = configurationId || getters.getScimConfiguration?.id;

            try {
                const scimConfiguration = await regenerateAPIKey(organizationId, configurationId);
                commit('setScimConfiguration', { scimConfiguration });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async removeScimConfiguration ({ commit, getters }, { organizationId, configurationId } = {}) {
            organizationId = organizationId || getters.getActive.id;
            configurationId = configurationId || getters.getScimConfiguration?.id;

            try {
                await removeScimConfiguration(organizationId, configurationId);
                commit('setScimConfiguration', { scimConfiguration: {} });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async updateIdPGroupPermissionsForSub (
            { commit, getters },
            { organizationId, configurationId, subscriptionId, payload } = {},
        ) {
            organizationId = organizationId || getters.getActive.id;
            configurationId = configurationId || getters.getScimConfiguration?.id;

            try {
                const scimConfiguration = await updateIdPGroupPermissionsForSub(
                    organizationId,
                    configurationId,
                    subscriptionId,
                    payload,
                );
                commit('setScimConfiguration', { scimConfiguration });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
    },
    getters: {
        getActive: (state) => state.active,
        getFlag: (state) => (flag) => !!get(state.active, `organizationFlags.${flag}`, false),
        getScimConfiguration: (state) => state.scimConfiguration,
    },
};

export default organization;
